/** @file mesh.setBinding: converts a mesh between rigid and skinned, exact at the setup pose (§6.7). */
import { z } from 'zod';
import type { Doc, Id, MeshAtt } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';
import { affine } from '@pose2d/math/affine';
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { MOUNT_REASONS } from '@pose2d/core/mount/mountReasons.data';
import type { CommandContext } from '../commandContext';
import { defineCommand } from '../defineCommand';
import { bindMatrix } from './bindMatrix';
import { invertChecked } from './invertChecked';
import { meshDraft } from './meshDraft';
import { meshSlotBone } from './meshSlotBone';
import { meshSlotBones } from './meshSlotBones';
import { setupWorldOf } from './setupWorldOf';
import { skinBlend } from './skinBlend';
import { transformDeformKeys } from './transformDeformKeys';
import { transformMeshPoints } from './transformMeshPoints';

type Geometry = MeshAtt['geometry'];
type WorldOf = (bone: Id) => Float64Array;
/** The new geometry plus the per-vertex affine T_v that maps old positions (and, linearly, old offsets) to new ones. */
type Converted = { geometry: Geometry; mats: Float64Array };
const STRIDE = POSE_LAYOUT.WORLD_STRIDE;

/** Rigid → skinned: T_v = W (slot bone setup world) for every vertex, one full-weight influence on that bone. */
function toSkinned(vertices: readonly number[], bone: Id, worldOf: WorldOf): Converted {
  const bind = bindMatrix(worldOf, bone, 'target');
  const count = vertices.length / 2;
  const mats = new Float64Array(count * STRIDE);
  for (let v = 0; v < count; v++) mats.set(bind, v * STRIDE);
  const weights = Array.from({ length: count }, (): [Id, number][] => [[bone, 1]]);
  return { geometry: { kind: 'skinned', bindVertices: transformMeshPoints(vertices, mats, 0, false), weights, bindPose: { [bone]: bind } }, mats };
}

/** Skinned → rigid: T_v = W⁻¹·M_v, with M_v the vertex's blended setup skinning affine. */
function toRigid(g: Extract<Geometry, { kind: 'skinned' }>, bone: Id, worldOf: WorldOf, path: string): Converted {
  const mats = skinBlend(g, worldOf, path);
  const inverse = invertChecked(worldOf(bone), 'target', `slot bone ${bone} has zero scale at setup`);
  for (let v = 0; v * STRIDE < mats.length; v++) {
    const m = mats.subarray(v * STRIDE, (v + 1) * STRIDE);
    affine.mul(m, inverse, m);
  }
  return { geometry: { kind: 'rigid', vertices: transformMeshPoints(g.bindVertices, mats, 0, false) }, mats };
}

/** `ref` resolved (NOT_FOUND); VALIDATION at `bone` when the skins show mesh `id` only in slots on other bones. */
function explicitBone(doc: Doc, ctx: CommandContext, id: Id, ref: string): Id {
  const bone = ctx.resolve('bone', ref);
  const used = meshSlotBones(doc, id);
  if (used.size > 0 && !used.has(bone)) {
    const names = [...used].map((b) => doc.bones[b]?.name ?? b).join(', ');
    throwError('VALIDATION', undefined, { path: 'bone', hint: `the slots showing this mesh sit on ${names}; binding to another bone would change the setup look (omit bone or pass one of those)` });
  }
  return bone;
}

/** mesh.setBinding: positions and deform keys are rewritten so the setup pose renders the same; `bone` picks the slot bone. */
export const command = defineCommand({
  name: 'mesh.setBinding',
  description: 'Convert a mesh between rigid (follows its slot bone) and skinned (weighted to bones), converting positions and deform keys so the setup pose looks the same; a new skinned mesh starts fully weighted to the slot bone, which `bone` (name or id) names and which otherwise comes from the skins and must be unique.',
  input: z.object({ target: z.string().min(1), binding: z.enum(['rigid', 'skinned']), bone: z.string().min(1).exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const { id, mesh } = meshDraft(draft, ctx, input.target);
    const chosen = input.bone === undefined ? undefined : explicitBone(draft, ctx, id, input.bone);
    const g = mesh.geometry;
    if (g.kind === input.binding) {
      ctx.warnings.push({ code: 'INFO', message: `${mesh.name} is already ${input.binding}; nothing changed`, path: 'binding' });
      return;
    }
    if (input.binding === 'skinned' && mesh.mount) {
      throwError('MOUNT_INVALID', { attachment: mesh.name, reason: MOUNT_REASONS.skinned }, { path: 'target', hint: 'unequip it first (socket.unequip)' });
    }
    const bone = chosen ?? meshSlotBone(draft, id);
    const worldOf = setupWorldOf(draft);
    const out = g.kind === 'rigid' ? toSkinned(g.vertices, bone, worldOf) : toRigid(g, bone, worldOf, `attachments.${id}.geometry`);
    mesh.geometry = out.geometry;
    transformDeformKeys(draft, id, out.mats);
    draft.meta.updatedAt = ctx.now;
  },
});
